import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ProductNutrientEntry, ValueQualifier, emptyNutrientRow } from '../types';
import { CodeNameOption, NutritionMasterData } from '../../../../app/services/api/orderManagementClient';
import {
  VALUE_QUALIFIER_LABEL_KEYS,
  NUTRITION_BASIS_TYPE_LABEL_KEYS,
  NUTRITION_PREPARATION_STATE_LABEL_KEYS,
  ANALYSIS_METHOD_LABEL_KEYS,
  getEnumLabel,
} from '../enumLabels';

interface NutritionalDataTabProps {
  nutrition: ProductNutrientEntry[];
  onChange: (nutrition: ProductNutrientEntry[]) => void;
  disabled?: boolean;
  referenceOptions?: NutritionMasterData | null;
}

const toDateValue = (value?: string | null): Dayjs | null => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const NutritionalDataTab: FC<NutritionalDataTabProps> = ({ nutrition, onChange, disabled, referenceOptions }) => {
  const { t } = useTranslation('productManagement');
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const updateRow = (index: number, patch: Partial<ProductNutrientEntry>) => {
    const next = nutrition.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const removeRow = (index: number) => {
    // Row identity is positional (key={index}); reset expanded state so a delete
    // can't leave a later row's expanded panel misattributed to its new position.
    setExpandedRows({});
    onChange(nutrition.filter((_, i) => i !== index));
  };

  const addRow = () => {
    // Expand the new row by default so mandatory fields inside the collapsed section
    // (Profile Id, Basis Type/Quantity/Unit) are visible before the user tries to save.
    setExpandedRows((prev) => ({ ...prev, [nutrition.length]: true }));
    onChange([...nutrition, emptyNutrientRow()]);
  };

  const toggleExpanded = (index: number) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="textLG"
          component="div"
          sx={{ fontWeight: 600 }}>
          {t('nutritionalInfo', 'Nutritional Info')}
        </Typography>
        <Button startIcon={<AddIcon />}
          onClick={addRow}
          disabled={disabled}
          size="md">
          {t('addNutrient', 'Add nutrient')}
        </Button>
      </Box>

      {nutrition.length === 0 && (
        <Typography variant="textSM"
          component="div"
          color="text.secondary"
          sx={{ mb: 3 }}>
          {t('noNutritionCaptured', 'No nutritional data captured yet.')}
        </Typography>
      )}

      {nutrition.map((row, index) => {
        const isExpanded = Boolean(expandedRows[index]);
        return (
          <Box key={index}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, pt: '12px', pr: 3, pb: 0, pl: 3, mb: 3 }}>
            <Grid container
              spacing={3}
              alignItems="center">
              <Grid item
                xs={12}
                sm={3}>
                {referenceOptions ? (
                  <Autocomplete<CodeNameOption>
                    options={referenceOptions.nutrients}
                    getOptionLabel={(option) => option.code}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    value={referenceOptions.nutrients.find((n) => n.code === row.nutrientCode) ?? null}
                    onChange={(_e, newValue) => updateRow(index, {
                      nutrientCode: newValue?.code ?? '',
                      nutrientName: newValue?.name ?? '',
                    })}
                    disabled={disabled}
                    size="small"
                    renderOption={(props, option) => (
                      <li {...props} 
                        key={option.code}>{option.code}</li>
                    )}
                    renderInput={(params) => <TextField {...params} 
                      label={t('nutrientCode', 'Code')} 
                      required />}
                  />
                ) : (
                  <TextField
                    label={t('nutrientCode', 'Code')}
                    value={row.nutrientCode}
                    onChange={(e) => updateRow(index, { nutrientCode: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    required
                  />
                )}
              </Grid>
              <Grid item
                xs={12}
                sm={3}>
                <TextField
                  label={t('nutrientName', 'Nutrient Name')}
                  value={row.nutrientName ?? ''}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item
                xs={5}
                sm={2}>
                <TextField
                  label={t('value', 'Value')}
                  type="number"
                  value={row.nutrientValue ?? ''}
                  onChange={(e) => updateRow(index, { nutrientValue: e.target.value === '' ? undefined : Number(e.target.value) })}
                  disabled={disabled}
                  size="small"
                  fullWidth
                  required
                />
              </Grid>
              <Grid item
                xs={3}
                sm={2}>
                {referenceOptions ? (
                  <Autocomplete<CodeNameOption>
                    options={referenceOptions.nutrientMeasureUoms}
                    getOptionLabel={(option) => option.name || option.code}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    value={referenceOptions.nutrientMeasureUoms.find((u) => u.code === row.valueUom) ?? null}
                    onChange={(_e, newValue) => updateRow(index, { valueUom: newValue?.code ?? '' })}
                    disabled={disabled}
                    size="small"
                    renderInput={(params) => <TextField {...params} 
                      label={t('unit', 'Unit')} 
                      required />}
                  />
                ) : (
                  <TextField
                    label={t('unit', 'Unit')}
                    value={row.valueUom ?? ''}
                    onChange={(e) => updateRow(index, { valueUom: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    required
                  />
                )}
              </Grid>
              <Grid item
                xs={4}
                sm={2}
                sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton size="small"
                  onClick={() => toggleExpanded(index)}
                  aria-label={isExpanded ? t('collapse', 'Collapse') : t('expand', 'Expand')}>
                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small"
                  onClick={() => removeRow(index)}
                  disabled={disabled}
                  aria-label={t('remove', 'Remove')}>
                  <DeleteOutlineIcon fontSize="small"
                    color='error' />
                </IconButton>
              </Grid>
            </Grid>

            <Collapse in={isExpanded}>
              <Grid container
                spacing={3}
                sx={{ mt: 0, pt: 0, pb: 0, pl: '12px', pr: '12px' }}>
                <Grid item
                  xs={12}
                  sm={4}>
                  <TextField
                    label={t('nutritionProfileId', 'Profile Id')}
                    value={row.nutritionProfileId ?? ''}
                    onChange={(e) => updateRow(index, { nutritionProfileId: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <FormControl fullWidth
                    size="small">
                    <InputLabel shrink>{t('valueQualifier', 'Value Qualifier')}</InputLabel>
                    <Select
                      value={row.valueQualifier ?? ''}
                      onChange={(e: SelectChangeEvent) => updateRow(index, { valueQualifier: e.target.value })}
                      displayEmpty
                      disabled={disabled}
                      size="small"
                      fullWidth
                      label={t('valueQualifier', 'Value Qualifier')}
                      notched
                      renderValue={(selected) => (selected ? getEnumLabel(t, VALUE_QUALIFIER_LABEL_KEYS, selected as string) : <em>{t('none', 'None')}</em>)}
                    >
                      <MenuItem value="">
                        <em>{t('none', 'None')}</em>
                      </MenuItem>
                      {/* Fall back to showing the row's current value even if it's not (yet) in the
                          reference-data list, so an already-saved value never renders blank/lost. */}
                      {Array.from(new Set([
                        ...(referenceOptions?.nutrientQualifiers ?? Object.values(ValueQualifier)),
                        ...(row.valueQualifier ? [row.valueQualifier] : []),
                      ])).map((qualifier) => (
                        <MenuItem key={qualifier}
                          value={qualifier}>
                          {getEnumLabel(t, VALUE_QUALIFIER_LABEL_KEYS, qualifier)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  {referenceOptions ? (
                    <Autocomplete<string>
                      options={referenceOptions.basisTypes}
                      getOptionLabel={(option) => getEnumLabel(t, NUTRITION_BASIS_TYPE_LABEL_KEYS, option)}
                      value={row.basisType || null}
                      onChange={(_e, newValue) => updateRow(index, { basisType: newValue ?? '' })}
                      disabled={disabled}
                      size="small"
                      renderInput={(params) => <TextField {...params} 
                        label={t('basisType', 'Basis Type')}
                        required />}
                    />
                  ) : (
                    <TextField
                      label={t('basisType', 'Basis Type')}
                      value={row.basisType ?? ''}
                      onChange={(e) => updateRow(index, { basisType: e.target.value })}
                      disabled={disabled}
                      size="small"
                      fullWidth
                      required
                    />
                  )}
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('basisQuantity', 'Basis Quantity')}
                    type="number"
                    value={row.basisQuantity ?? ''}
                    onChange={(e) => updateRow(index, { basisQuantity: e.target.value === '' ? undefined : Number(e.target.value) })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  {referenceOptions ? (
                    <Autocomplete<CodeNameOption>
                      options={referenceOptions.nutrientBasisUoms}
                      getOptionLabel={(option) => option.name || option.code}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      value={referenceOptions.nutrientBasisUoms.find((u) => u.code === row.basisUom) ?? null}
                      onChange={(_e, newValue) => updateRow(index, { basisUom: newValue?.code ?? '' })}
                      disabled={disabled}
                      size="small"
                      renderInput={(params) => <TextField {...params} 
                        label={t('basisUom', 'Basis Unit')} 
                        required />}
                    />
                  ) : (
                    <TextField
                      label={t('basisUom', 'Basis Unit')}
                      value={row.basisUom ?? ''}
                      onChange={(e) => updateRow(index, { basisUom: e.target.value })}
                      disabled={disabled}
                      size="small"
                      fullWidth
                      required
                    />
                  )}
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  {referenceOptions ? (
                    <Autocomplete<string>
                      options={referenceOptions.preparationStates}
                      getOptionLabel={(option) => getEnumLabel(t, NUTRITION_PREPARATION_STATE_LABEL_KEYS, option)}
                      value={row.preparationState || null}
                      onChange={(_e, newValue) => updateRow(index, { preparationState: newValue ?? '' })}
                      disabled={disabled}
                      size="small"
                      renderInput={(params) => <TextField {...params} 
                        label={t('preparationState', 'Preparation State')} />}
                    />
                  ) : (
                    <TextField
                      label={t('preparationState', 'Preparation State')}
                      value={row.preparationState ?? ''}
                      onChange={(e) => updateRow(index, { preparationState: e.target.value })}
                      disabled={disabled}
                      size="small"
                      fullWidth
                    />
                  )}
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <TextField
                    label={t('servingSize', 'Serving Size')}
                    type="number"
                    value={row.servingSize ?? ''}
                    onChange={(e) => updateRow(index, { servingSize: e.target.value === '' ? undefined : Number(e.target.value) })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  {referenceOptions ? (
                    <Autocomplete<CodeNameOption>
                      options={referenceOptions.servingUoms}
                      getOptionLabel={(option) => option.name || option.code}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      value={referenceOptions.servingUoms.find((u) => u.code === row.servingUom) ?? null}
                      onChange={(_e, newValue) => updateRow(index, { servingUom: newValue?.code ?? '' })}
                      disabled={disabled}
                      size="small"
                      renderInput={(params) => <TextField {...params} 
                        label={t('servingUom', 'Serving Unit')} />}
                    />
                  ) : (
                    <TextField
                      label={t('servingUom', 'Serving Unit')}
                      value={row.servingUom ?? ''}
                      onChange={(e) => updateRow(index, { servingUom: e.target.value })}
                      disabled={disabled}
                      size="small"
                      fullWidth
                    />
                  )}
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  {referenceOptions ? (
                    <Autocomplete<string>
                      options={referenceOptions.analysisMethods}
                      getOptionLabel={(option) => getEnumLabel(t, ANALYSIS_METHOD_LABEL_KEYS, option)}
                      value={row.analysisMethod || null}
                      onChange={(_e, newValue) => updateRow(index, { analysisMethod: newValue ?? '' })}
                      disabled={disabled}
                      size="small"
                      renderInput={(params) => <TextField {...params} 
                        label={t('analysisMethod', 'Analysis Method')} />}
                    />
                  ) : (
                    <TextField
                      label={t('analysisMethod', 'Analysis Method')}
                      value={row.analysisMethod ?? ''}
                      onChange={(e) => updateRow(index, { analysisMethod: e.target.value })}
                      disabled={disabled}
                      size="small"
                      fullWidth
                    />
                  )}
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <TextField
                    label={t('sourceReference', 'Source Reference')}
                    value={row.sourceReference ?? ''}
                    onChange={(e) => updateRow(index, { sourceReference: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={3}>
                  <FormControl fullWidth
                    size="small">
                    <InputLabel shrink>{t('supplierConfirmed', 'Supplier Confirmed')}</InputLabel>
                    <Select
                      value={row.supplierConfirmed ?? ''}
                      onChange={(e: SelectChangeEvent) => updateRow(index, { supplierConfirmed: e.target.value })}
                      displayEmpty
                      disabled={disabled}
                      size="small"
                      fullWidth
                      label={t('supplierConfirmed', 'Supplier Confirmed')}
                      notched
                    >
                      <MenuItem value=""><em>{t('none', 'None')}</em></MenuItem>
                      <MenuItem value="TRUE">{t('yes', 'Yes')}</MenuItem>
                      <MenuItem value="FALSE">{t('no', 'No')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <DatePicker
                    label={t('effectiveFrom', 'Effective From')}
                    value={toDateValue(row.effectiveFrom)}
                    onChange={(newValue) => updateRow(index, { effectiveFrom: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : undefined })}
                    disabled={disabled}
                    maxDate={toDateValue(row.effectiveTo) ?? undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true }, field: { clearable: true } }}
                  />
                </Grid>
                <Grid item
                  xs={6}
                  sm={3}>
                  <DatePicker
                    label={t('effectiveTo', 'Effective To')}
                    value={toDateValue(row.effectiveTo)}
                    onChange={(newValue) => updateRow(index, { effectiveTo: newValue?.isValid() ? newValue.format('YYYY-MM-DD') : undefined })}
                    disabled={disabled}
                    minDate={toDateValue(row.effectiveFrom) ?? undefined}
                    slotProps={{ textField: { size: 'small', fullWidth: true }, field: { clearable: true } }}
                  />
                </Grid>
                <Grid item
                  xs={12}>
                  <TextField
                    label={t('notes', 'Notes')}
                    value={row.notes ?? ''}
                    onChange={(e) => updateRow(index, { notes: e.target.value })}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    multiline
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default NutritionalDataTab;
